import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { UserMenu } from '../components/UserMenu';
import { 
  Stethoscope, 
  Shield, 
  Lock, 
  FileText,
  ShieldCheck,
  Brain,
  Warning,
  FirstAid,
  ChatCircle,
  MagnifyingGlass,
  ImageSquare,
  UserCircle,
  ArrowRight,
  CheckCircle,
  Heart
} from '@phosphor-icons/react';
import { SOSButton } from '../components/SOSButton';
import { Footer } from '../components/Footer';

const CONSULTATION_TYPES = [
  {
    id: 'consultation',
    title: 'Consultație Digitală Asistată',
    description: 'Evaluarea simptomelor și recomandări personalizate generate cu ajutorul tehnologiei S8NTH Strategic Engine.',
    icon: Stethoscope,
    color: 'text-primary',
    bgColor: 'bg-primary/10'
  },
  {
    id: 'emergency',
    title: 'Evaluare Rapidă a Urgențelor',
    description: 'Analiză rapidă a simptomelor și estimarea nivelului de risc. În cazul unei urgențe medicale, utilizatorul va fi îndrumat către serviciul 112 sau cea mai apropiată unitate medicală.',
    icon: FirstAid,
    color: 'text-destructive',
    bgColor: 'bg-destructive/10',
    warning: 'Dacă simptomele sugerează o urgență medicală majoră (durere toracică severă, dificultăți importante de respirație, pierderea stării de conștiență, semne de accident vascular cerebral etc.), apelați imediat serviciul de urgență 112. MEDICULDIGITAL nu înlocuiește serviciile medicale de urgență.'
  },
  {
    id: 'second_opinion',
    title: 'Second Opinion Digital',
    description: 'Analiza unui diagnostic existent și furnizarea unei opinii medicale suplimentare cu caracter informativ.',
    icon: ChatCircle,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50'
  },
  {
    id: 'lab_results',
    title: 'Interpretare Analize Medicale',
    description: 'Explicarea rezultatelor analizelor medicale într-un limbaj clar și ușor de înțeles.',
    icon: MagnifyingGlass,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  },
  {
    id: 'imaging',
    title: 'Interpretare Investigații Imagistice',
    description: 'Analiza rapoartelor și imaginilor medicale încărcate de utilizator.',
    icon: ImageSquare,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  {
    id: 'specialist',
    title: 'Programare către Medic Specialist',
    description: 'Solicitare de programare către un medic colaborator atunci când este necesară evaluarea medicală directă.',
    icon: UserCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50'
  }
];

const REQUIRED_CHECKBOXES = [
  { id: 'terms', label: 'Am citit și accept Termenii și Condițiile.', link: '/terms' },
  { id: 'gdpr', label: 'Am citit și accept Politica GDPR.', link: '/gdpr' },
  { id: 'security', label: 'Am citit și accept Politica de Securitate și Confidențialitate.', link: '/security-policy' },
  { id: 'documents', label: 'Confirm că documentele și informațiile medicale încărcate îmi aparțin și sunt reale.' },
  { id: 'understanding', label: 'Confirm că am înțeles că MEDICULDIGITAL nu înlocuiește consultația, examinarea clinică, diagnosticul sau tratamentul stabilit de un medic.' }
];

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('welcome'); // 'welcome', 'consultation-type'
  const [acceptedTerms, setAcceptedTerms] = useState({});
  const [selectedConsultationType, setSelectedConsultationType] = useState(null);
  const [hasAcceptedBefore, setHasAcceptedBefore] = useState(false);

  useEffect(() => {
    // Check if user has already accepted terms in this session
    const accepted = localStorage.getItem('termsAccepted');
    if (accepted === 'true') {
      setHasAcceptedBefore(true);
      setStep('consultation-type');
    }
  }, []);

  const allTermsAccepted = REQUIRED_CHECKBOXES.every(cb => acceptedTerms[cb.id]);

  const handleContinueToConsultationType = () => {
    localStorage.setItem('termsAccepted', 'true');
    setStep('consultation-type');
  };

  const handleContinueToConsultation = () => {
    navigate('/consultation', { state: { consultationType: selectedConsultationType } });
  };

  const toggleCheckbox = (id) => {
    setAcceptedTerms(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen medical-gradient flex flex-col" data-testid="dashboard-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 text-primary">
              <Stethoscope weight="fill" className="w-7 h-7" />
              <div className="hidden sm:block">
                <span className="text-lg font-bold">MEDICULDIGITAL</span>
                <span className="text-xs block text-muted-foreground">Platforma Medicală S8NTH</span>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="hidden md:flex items-center gap-3">
              <span className="trust-badge">
                <Lock weight="fill" className="w-3.5 h-3.5" />
                Date medicale criptate
              </span>
              <span className="trust-badge">
                <Shield weight="fill" className="w-3.5 h-3.5" />
                GDPR
              </span>
            </div>

            {/* User Menu */}
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* STEP 1: Welcome & Terms */}
          {step === 'welcome' && (
            <div className="space-y-8 animate-in fade-in duration-500">
              {/* Hero Section */}
              <div className="text-center space-y-4">
                <h1 className="text-4xl sm:text-5xl font-bold text-primary tracking-tight">
                  MEDICULDIGITAL
                </h1>
                <p className="text-lg text-muted-foreground">
                  Platforma Medicală S8NTH
                </p>
                <p className="text-sm text-foreground/80 max-w-2xl mx-auto leading-relaxed">
                  PRIMA PLATFORMĂ MEDICALĂ DIGITALĂ CARE UTILIZEAZĂ CONSULTAȚII ASISTATE 
                  CU TEHNOLOGIA S8NTH STRATEGIC ENGINE.
                </p>
                <p className="text-xs text-muted-foreground">
                  Consultații Digitale Asistate • Analiză medicală inteligentă • Second Opinion Digital • Telemedicină
                </p>
              </div>

              {/* Welcome Message */}
              <div className="premium-card p-6 text-center">
                <h2 className="text-xl font-semibold text-foreground mb-2">Bine ați venit!</h2>
                <p className="text-muted-foreground">
                  Înainte de începerea consultației vă rugăm să citiți și să acceptați documentele 
                  obligatorii privind utilizarea platformei și prelucrarea datelor medicale.
                </p>
              </div>

              {/* Important Information */}
              <div className="premium-card p-6 border-l-4 border-l-amber-500">
                <div className="flex items-start gap-3">
                  <Warning weight="fill" className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-foreground mb-3">INFORMARE IMPORTANTĂ</h3>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>
                        MEDICULDIGITAL utilizează tehnologia S8NTH Strategic Engine pentru analiza informațiilor 
                        și documentelor medicale furnizate de utilizator și generează recomandări și 
                        informații cu caracter orientativ.
                      </p>
                      <p className="font-medium text-foreground">
                        UTILIZAREA MEDICULDIGITAL NU ÎNLOCUIEȘTE CONSULTAȚIA, EXAMINAREA CLINICĂ, 
                        DIAGNOSTICUL SAU TRATAMENTUL STABILIT DE UN MEDIC.
                      </p>
                      <p className="text-destructive">
                        În cazul unei urgențe medicale sau al unor simptome severe, apelați imediat 
                        serviciul de urgență 112 sau prezentați-vă la cea mai apropiată Unitate de 
                        Primiri Urgențe.
                      </p>
                      <p className="italic">
                        Prin continuarea utilizării platformei confirmați că ați citit și ați înțeles 
                        această informare.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legal Documents Cards */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Documente Legale</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { title: 'Termeni și Condiții', link: '/terms', icon: FileText },
                    { title: 'Politica GDPR', link: '/gdpr', icon: Shield },
                    { title: 'Politica de Securitate', link: '/security-policy', icon: Lock },
                    { title: 'Politica AI', link: '/ai-policy', icon: Brain }
                  ].map((doc) => (
                    <a
                      key={doc.link}
                      href={doc.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="premium-card p-4 flex flex-col items-center text-center gap-2 hover:border-primary/50"
                    >
                      <doc.icon weight="duotone" className="w-8 h-8 text-primary" />
                      <span className="text-xs font-medium text-foreground">{doc.title}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Required Checkboxes */}
              <div className="premium-card p-6">
                <h3 className="font-semibold text-foreground mb-4">Acceptare Obligatorie</h3>
                <div className="space-y-4">
                  {REQUIRED_CHECKBOXES.map((checkbox) => (
                    <div key={checkbox.id} className="flex items-start gap-3">
                      <Checkbox
                        id={checkbox.id}
                        checked={acceptedTerms[checkbox.id] || false}
                        onCheckedChange={() => toggleCheckbox(checkbox.id)}
                        className="mt-0.5"
                      />
                      <label 
                        htmlFor={checkbox.id} 
                        className="text-sm text-muted-foreground cursor-pointer leading-relaxed"
                      >
                        {checkbox.link ? (
                          <>
                            {checkbox.label.split(checkbox.label.includes('Termenii') ? 'Termenii și Condițiile' : checkbox.label.includes('GDPR') ? 'Politica GDPR' : 'Politica de Securitate')[0]}
                            <a 
                              href={checkbox.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {checkbox.label.includes('Termenii') ? 'Termenii și Condițiile' : checkbox.label.includes('GDPR') ? 'Politica GDPR' : 'Politica de Securitate și Confidențialitate'}
                            </a>.
                          </>
                        ) : (
                          checkbox.label
                        )}
                      </label>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handleContinueToConsultationType}
                  disabled={!allTermsAccepted}
                  className="w-full mt-6 h-12 text-base gap-2"
                  data-testid="continue-to-consultation-btn"
                >
                  CONTINUĂ CĂTRE CONSULTAȚIE
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  { icon: ShieldCheck, text: 'GDPR Compliant' },
                  { icon: Lock, text: 'Date Medicale Criptate' },
                  { icon: Shield, text: 'Secure Cloud' },
                  { icon: Brain, text: 'Inteligență Artificială Responsabilă' }
                ].map((badge, idx) => (
                  <span key={idx} className="trust-badge">
                    <badge.icon weight="fill" className="w-4 h-4 text-emerald-600" />
                    {badge.text}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* STEP 2: Consultation Type Selection */}
          {step === 'consultation-type' && (
            <div className="space-y-8 animate-in fade-in duration-500">
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold text-primary">Alege tipul Consultației Digitale Asistate</h1>
                <p className="text-muted-foreground">Selectați serviciul medical digital care răspunde cel mai bine nevoilor dumneavoastră.</p>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {CONSULTATION_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedConsultationType(type.id)}
                    className={`consultation-card ${selectedConsultationType === type.id ? 'selected' : ''}`}
                    data-testid={`consultation-type-${type.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${type.bgColor}`}>
                        <type.icon weight="duotone" className={`w-7 h-7 ${type.color}`} />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-semibold text-foreground mb-1">{type.title}</h3>
                        <p className="text-sm text-muted-foreground">{type.description}</p>
                      </div>
                      {selectedConsultationType === type.id && (
                        <CheckCircle weight="fill" className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    {type.warning && selectedConsultationType === type.id && (
                      <div className="mt-4 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                        <p className="text-xs text-destructive">{type.warning}</p>
                      </div>
                    )}
                  </button>
                ))}
              </div>

              <Button
                onClick={handleContinueToConsultation}
                disabled={!selectedConsultationType}
                className="w-full h-12 text-base gap-2"
                data-testid="continue-to-consultation-page"
              >
                Continuă către Consultația Digitală Asistată
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Button>

              {/* Back to terms option */}
              <p className="text-center text-sm text-muted-foreground">
                <button 
                  onClick={() => setStep('welcome')}
                  className="text-primary hover:underline"
                >
                  ← Revino la documentele legale
                </button>
              </p>
            </div>
          )}

        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* SOS Button */}
      <SOSButton />
    </div>
  );
}
